import React, { useCallback, useEffect, useState } from 'react';
import PolicyContainer from './PolicyContainer';
import Button from '../Button';
import { policyService } from '../../api';
import type { PolicyViewModel } from '../../api/types/policy';

export interface PolicyViewModalProps {
  show: boolean;
  onClose: () => void;
  /** Route slug such as `kullanici-sozlesmesi`. */
  routeSlug: string;
  fallbackTitle?: string;
}

/**
 * Read-only policy overlay — keeps underlying form state intact (auth/register flows).
 */
const PolicyViewModal: React.FC<PolicyViewModalProps> = ({
  show,
  onClose,
  routeSlug,
  fallbackTitle = 'Sözleşme',
}) => {
  const fetchPolicy = useCallback(async (): Promise<PolicyViewModel | null> => {
    const result = await policyService.getByRouteSlug(routeSlug);
    return result.isSuccess && result.data ? result.data : null;
  }, [routeSlug]);

  useEffect(() => {
    if (!show) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.classList.add('modal-open');
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.classList.remove('modal-open');
    };
  }, [show, onClose]);

  if (!show) return null;

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="policyViewModalTitle"
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="policyViewModalTitle">
                {fallbackTitle}
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Kapat"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              <PolicyContainer fetchPolicy={fetchPolicy} fallbackTitle={fallbackTitle} />
            </div>
            <div className="modal-footer">
              <Button type="button" className="btn btn-primary" onClick={onClose}>
                Kapat
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} aria-hidden="true" />
    </>
  );
};

export default PolicyViewModal;
